export type InternetHealthSource =
  | "network-information"
  | "latency-test"
  | "fallback";

export type InternetHealthSnapshot = {
  source?: InternetHealthSource;
  lastCheckedAt?: string | null;
  latencyMs?: number | null;
  effectiveType?: string | null;
  downlinkMbps?: number | null;
  online?: boolean | null;
};

export type PortalSystemStatusSnapshot = {
  service: string | null;
  environment: string | null;
  databaseStatus: string | null;
  licenseStatus: string | null;
  uptime: string | null;
};

type NavigatorConnectionLike = {
  effectiveType?: unknown;
  downlink?: unknown;
  rtt?: unknown;
};

type NavigatorLike = {
  connection?: unknown;
  mozConnection?: unknown;
  webkitConnection?: unknown;
};

function asNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function hasData(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (value && typeof value === "object") {
    return Object.keys(value as Record<string, unknown>).length > 0;
  }

  return asNonEmptyString(value) !== null || asFiniteNumber(value) !== null;
}

function humanizeToken(value: string | null): string | null {
  if (!value) {
    return null;
  }

  return value
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatNetworkType(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return "Awaiting sample";
  }

  if (normalized === "slow-2g") {
    return "Slow 2G";
  }

  if (/^\d+g$/.test(normalized)) {
    return normalized.toUpperCase();
  }

  return humanizeToken(normalized) || "Awaiting sample";
}

export function formatUptimeSeconds(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const days = Math.floor(safeSeconds / 86_400);
  const hours = Math.floor((safeSeconds % 86_400) / 3_600);
  const minutes = Math.floor((safeSeconds % 3_600) / 60);
  const seconds = safeSeconds % 60;
  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days}d`);
  }

  if (hours > 0 || days > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0 || hours > 0 || days > 0) {
    parts.push(`${minutes}m`);
  }

  if (days === 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join(" ");
}

export function normalizeSystemStatusPayload(
  payload: unknown,
): PortalSystemStatusSnapshot {
  const raw =
    payload && typeof payload === "object"
      ? (payload as Record<string, unknown>)
      : {};
  const databaseRaw =
    raw.database && typeof raw.database === "object"
      ? (raw.database as Record<string, unknown>)
      : null;
  const licenseRaw =
    raw.license && typeof raw.license === "object"
      ? (raw.license as Record<string, unknown>)
      : null;
  const explicitDatabaseStatus = asNonEmptyString(databaseRaw?.status);
  const databaseStatus =
    explicitDatabaseStatus ||
    (databaseRaw &&
    (hasData(databaseRaw.tables) || hasData(databaseRaw.totalRecords))
      ? "Reachable"
      : null);

  return {
    service: asNonEmptyString(raw.service),
    environment: asNonEmptyString(raw.environment),
    databaseStatus,
    licenseStatus: humanizeToken(asNonEmptyString(licenseRaw?.status)),
    uptime:
      asNonEmptyString(raw.uptime) ||
      (asFiniteNumber(raw.uptime) !== null
        ? formatUptimeSeconds(asFiniteNumber(raw.uptime) as number)
        : null),
  };
}

export function getNetworkInformationConnection(
  navigatorLike?: NavigatorLike | null,
): NavigatorConnectionLike | null {
  const connection =
    navigatorLike?.connection ||
    navigatorLike?.mozConnection ||
    navigatorLike?.webkitConnection;

  return connection && typeof connection === "object"
    ? (connection as NavigatorConnectionLike)
    : null;
}

function estimateNetworkTypeFromLatency(latencyMs: number): string {
  if (latencyMs < 150) {
    return "4G-like";
  }

  if (latencyMs < 300) {
    return "3G-like";
  }

  if (latencyMs < 700) {
    return "2G-like";
  }

  return "Slow 2G-like";
}

export function describeInternetEffectiveType(
  snapshot?: InternetHealthSnapshot | null,
): string {
  if (snapshot?.online === false) {
    return "Offline";
  }

  if (typeof snapshot?.effectiveType === "string" && snapshot.effectiveType) {
    return formatNetworkType(snapshot.effectiveType);
  }

  if (
    snapshot?.source === "latency-test" &&
    typeof snapshot.latencyMs === "number" &&
    Number.isFinite(snapshot.latencyMs)
  ) {
    return `Estimated ${estimateNetworkTypeFromLatency(snapshot.latencyMs)}`;
  }

  if (snapshot?.source === "fallback") {
    return "Browser estimate unavailable";
  }

  return "Awaiting sample";
}

export function describeInternetDownlink(
  snapshot?: InternetHealthSnapshot | null,
): string {
  if (snapshot?.online === false) {
    return "Offline";
  }

  if (
    typeof snapshot?.downlinkMbps === "number" &&
    Number.isFinite(snapshot.downlinkMbps)
  ) {
    return `${snapshot.downlinkMbps.toFixed(1)} Mbps`;
  }

  if (snapshot?.source === "latency-test") {
    return "Not exposed by browser";
  }

  if (snapshot?.source === "fallback") {
    return "Awaiting browser support";
  }

  return "Awaiting sample";
}